import * as React from 'react'
import {ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {
    NatOwnershipTransferIntermediaryInfo,
    NatOwnershipTransferIntermediaryLocationState,
} from './intermediary/NatGuestIntermediaryInterface'
import NatGuestIntermediaryConfirm from './intermediary/NatGuestIntermediaryConfirm'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {Nullable} from '../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {
    GuestApplyButton,
    GuestBackButton,
    GuestPlainLinkStyledBackButton,
    PageTitle,
} from '../common/NatOwnershipTransfersCommon'
import {OWNERSHIP_TRANSFER_STATUS} from '../../../../CodeMaster'
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatOwnershipTransferIntermediaryDetail} from '../repository/NatGetOwnershipTransferIntermediary'
import {NatPostIntermediaryResponse} from '../repository/NatPostIntermediaryRequest'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    applyId?: string
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<NatOwnershipTransferIntermediaryLocationState>
}

interface States {
    intermediaryDetail: NatOwnershipTransferIntermediaryInfo
    isLoadCompleted: boolean
}

class NatGuestIntermediaryConfirmPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            intermediaryDetail: {
                applyStatus: '',
                ownershipTransferDate: '',
                accessExpirationDate: '',
                companyName: '',
                zipCode: '',
                prefecture: '',
                address2: '',
                address3: '',
                buildingRoomNumber: '',
                representative: '',
                phoneNumber: '',
                mailAddress: '',
                mailAddressConfirm: '',
                version: 0,
            },
            isLoadCompleted: false,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleEditButtonOnClick = this.handleEditButtonOnClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        let intermediaryInfo = null
        if (!this.props.location.state?.applyInfo && this.props.applyId) {
            // 取得APIを呼んでServerからの取得情報を使用
            const apiResponse = await this.getOwnershipTransferIntermediary(this.props.applyId)
            if (apiResponse) {
                intermediaryInfo = {
                    applyStatus: apiResponse.applyStatus,
                    ownershipTransferDate: apiResponse.ownershipTransferDate,
                    accessExpirationDate: apiResponse.accessExpirationDate,
                    companyName: apiResponse.companyName,
                    zipCode: apiResponse.zipCode,
                    prefecture: apiResponse.prefecture,
                    address2: apiResponse.address2,
                    address3: apiResponse.address3,
                    buildingRoomNumber: apiResponse.buildingRoomNumber,
                    representative: apiResponse.representative,
                    phoneNumber: apiResponse.phoneNumber,
                    mailAddress: apiResponse.mailAddress,
                    mailAddressConfirm: apiResponse.mailAddress,
                    version: apiResponse.version,
                }
            }
        } else {
            // 引継情報を使用
            intermediaryInfo = {
                applyStatus: OWNERSHIP_TRANSFER_STATUS.ISSUED.value,
                ownershipTransferDate: this.props.location.state.applyInfo.ownershipTransferDate,
                accessExpirationDate: this.props.location.state.applyInfo.accessExpirationDate,
                companyName: this.props.location.state.applyInfo.companyName,
                zipCode: this.props.location.state.applyInfo.zipCode,
                prefecture: this.props.location.state.applyInfo.prefecture,
                address2: this.props.location.state.applyInfo.address2,
                address3: this.props.location.state.applyInfo.address3,
                buildingRoomNumber: this.props.location.state.applyInfo.buildingRoomNumber,
                representative: this.props.location.state.applyInfo.representative,
                phoneNumber: this.props.location.state.applyInfo.phoneNumber,
                mailAddress: this.props.location.state.applyInfo.mailAddress,
                mailAddressConfirm: this.props.location.state.applyInfo.mailAddressConfirm,
                version: this.props.location.state.applyInfo.version,
            }
        }
        if (intermediaryInfo) {
            this.setState({
                intermediaryDetail: intermediaryInfo,
            })
        }
    }

    async getOwnershipTransferIntermediary(applyId: string): Promise<Nullable<NatOwnershipTransferIntermediaryDetail>> {
        const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransferIntermediary
                .bind(ownershipTransfersRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    // 修正するボタンを押下
    handleEditButtonOnClick(): void {
        this.props.navigate(
            `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/input/${this.props.applyId ?? ''}`,
            {
                replace: true,
                state: {
                    applyInfo: this.state.intermediaryDetail,
                    isCompleteFlow: this.props.location.state?.isCompleteFlow,
                },
            },
        )
    }

    async handleRegisterButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        const {ownershipTransfersRepo, condominiumCd, applyId} = this.props
        const {
            ownershipTransferDate, companyName, zipCode,
            prefecture, address2, address3,
            buildingRoomNumber, representative, phoneNumber,
            mailAddress, mailAddressConfirm, version,
        } = this.state.intermediaryDetail

        const request = {
            applyId: applyId ?? null,
            ownershipTransferDate: ownershipTransferDate,
            companyName: companyName,
            zipCode: zipCode,
            prefecture: prefecture,
            address2: address2,
            address3: address3,
            buildingRoomNumber: buildingRoomNumber,
            representative: representative,
            phoneNumber: phoneNumber,
            mailAddress: mailAddress,
            mailAddressConfirm: mailAddressConfirm,
            version: version,
            confirmed: true,
        }

        try {
            const response: Nullable<NatPostIntermediaryResponse> =
                await this.props.asyncCallFunction(
                    ownershipTransfersRepo.applyOwnershipTransferIntermediary.bind(
                        ownershipTransfersRepo, condominiumCd, request),
                    () => {
                        this.props.navigate(`/guest/error`, {replace: true})
                    },
                )
            if (response && response.requestError === null) {
                // 提出完了画面
                this.props.navigate(
                    `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/complete`,
                    {
                        replace: true,
                        state: {
                            applyId: response.details.applyId,
                            accessExpirationDate: response.details.accessExpirationDate,
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleBackLinkOnClick(): Promise<void> {
        const locationState: NatOwnershipTransferIntermediaryLocationState = this.props.location.state
        if (this.props.location.state?.isApply) {
            this.props.navigate(
                `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/input/${this.props.applyId ?? ''}`,
                {
                    replace: true,
                    state: JSON.parse(JSON.stringify(locationState)),
                },
            )
        } else {
            const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props
            await eventEmitter.showLoading()
            try {
                const accessKeyByLoginUser = await this.props.asyncCallFunction(
                    ownershipTransfersRepo.getAccessKeyByLoginUser.bind(ownershipTransfersRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                const accessKey = accessKeyByLoginUser?.details?.accessKey ?? ''
                this.props.navigate(`/guest/mypage?accessKey=${accessKey}`, {replace: true})
            } finally {
                eventEmitter.closeLoading()
            }
        }
    }


    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                {(!this.props.location.state?.isCompleteFlow || this.props.location.state?.isApply) &&
                    <GuestPlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>
                        戻る
                    </GuestPlainLinkStyledBackButton>
                }
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容の確認
                </PageTitle>
                <Box height={'20px'}/>
                <NatGuestIntermediaryConfirm
                    {...this.state.intermediaryDetail}
                    applyId={this.props.applyId}
                    isApply={this.props.location.state?.isApply}
                />
                {this.props.location.state?.isApply &&
                    <>
                        <Box height={'20px'}/>
                        <GuestApplyButton onClick={this.handleRegisterButtonClick}>
                            申請を提出する
                        </GuestApplyButton>
                        <Box height={'10px'}/>
                    </>
                }
                {(this.state.intermediaryDetail.applyStatus === OWNERSHIP_TRANSFER_STATUS.ISSUED.value ||
                        this.state.intermediaryDetail.applyStatus === OWNERSHIP_TRANSFER_STATUS.INPUTTING.value) &&
                    <>
                        <Box height={'20px'}/>
                        <GuestBackButton
                            onClick={this.handleEditButtonOnClick}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    修正する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </GuestBackButton>
                    </>
                }
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestIntermediaryConfirmPage)
